import React, { useState, useEffect, useCallback } from "react";
import {
  PlusCircle,
  Trash2,
  Edit2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  X,
  Calendar,
  Upload,
  Image,
  CheckCircle,
  Search,
} from "lucide-react";
import marathiService from '../../services/marathiService';

// Custom Alert Component
const Alert = ({ children, variant, className = "" }) => {
  const baseStyles = "flex w-full rounded-lg border p-4";
  const variantStyles =
    variant === "destructive"
      ? "border-red-200 bg-red-50 text-red-600"
      : "border-gray-200 bg-gray-50 text-gray-600";

  return (
    <div className={`${baseStyles} ${variantStyles} ${className}`}>
      {children}
    </div>
  );
};

const AlertDescription = ({ children, className = "" }) => (
  <div className={`text-sm ${className}`}>{children}</div>
);

const MarathiPhotoGallery = () => {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(6);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);

  // Form Data
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    photos: [],
    thumbnail: null,
  });

  // Temp state for inputs
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedThumbnailIndex, setSelectedThumbnailIndex] = useState(0);

  const fetchGalleries = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await marathiService.gallery.getAll({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
      });
      
      if (Array.isArray(response)) {
        setGalleries(response);
        setTotalPages(Math.ceil(response.length / itemsPerPage) || 1);
      } else if (response && response.data) {
        setGalleries(response.data);
        setTotalPages(response.totalPages || Math.ceil(response.data.length / itemsPerPage) || 1);
      } else {
        console.error('Unexpected response format:', response);
        setGalleries([]);
        setTotalPages(1);
      }
    } catch (err) {
      console.error('Error fetching photo galleries:', err);
      setError(err.message || "Failed to fetch photo galleries");
      setGalleries([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm]);

  useEffect(() => {
    fetchGalleries();
  }, [fetchGalleries]);

  // Clear success and error messages after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await marathiService.gallery.delete(selectedGallery._id);
      setSuccess("Gallery deleted successfully!");
      setShowDeleteModal(false);
      fetchGalleries();
    } catch (err) {
      console.error('Error deleting gallery:', err);
      setError(err.message || "Failed to delete gallery");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      setError("Title is required");
      return;
    }
    
    if (formData.photos.length === 0) {
      setError("At least one photo is required");
      return;
    }
    
    try {
      setLoading(true);
      setError("");
      
      const dataToSubmit = {
        ...formData,
        thumbnail: formData.thumbnail || (formData.photos.length > 0 ? formData.photos[0] : null)
      };
      
      if (selectedGallery) {
        await marathiService.gallery.update(
          selectedGallery._id,
          dataToSubmit
        );
        setSuccess("Gallery updated successfully!");
      } else {
        await marathiService.gallery.create(dataToSubmit);
        setSuccess("Gallery created successfully!");
      }
      setShowFormModal(false);
      fetchGalleries();
    } catch (err) {
      console.error('Error saving gallery:', err);
      setError(err.message || "Failed to save gallery");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const removePhoto = (index) => {
    const updatedPhotos = [...formData.photos];
    const removedPhoto = updatedPhotos.splice(index, 1)[0];
    
    // If removed photo was the thumbnail, set thumbnail to first remaining photo
    if (formData.thumbnail && formData.thumbnail.imageData === removedPhoto.imageData && updatedPhotos.length > 0) {
      setFormData({
        ...formData,
        photos: updatedPhotos,
        thumbnail: updatedPhotos[0]
      });
      setSelectedThumbnailIndex(0);
    } else {
      setFormData({
        ...formData,
        photos: updatedPhotos,
      });
      
      // Adjust thumbnail index if needed
      if (selectedThumbnailIndex >= updatedPhotos.length && updatedPhotos.length > 0) {
        setSelectedThumbnailIndex(updatedPhotos.length - 1);
        setFormData(prev => ({
          ...prev,
          thumbnail: updatedPhotos[updatedPhotos.length - 1]
        }));
      }
    }
  };

  const setThumbnail = (photo, index) => {
    setFormData({
      ...formData,
      thumbnail: photo
    });
    setSelectedThumbnailIndex(index);
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    try {
      setUploadLoading(true);
      setError("");
      setSuccess("");

      // Validate file types and sizes
      const validFiles = files.filter(file => {
        const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        const maxSize = 5 * 1024 * 1024; // 5MB
        
        if (!validTypes.includes(file.type)) {
          setError(`Invalid file type: ${file.name}. Only JPG, PNG, GIF, and WEBP are allowed.`);
          return false;
        }
        
        if (file.size > maxSize) {
          setError(`File too large: ${file.name}. Maximum size is 5MB.`);
          return false;
        }
        
        return true;
      });

      if (validFiles.length === 0) {
        return;
      }

      const response = await marathiService.upload.multiple(validFiles);

      if (response && response.success && response.data) {
        const newPhotos = response.data;

        setFormData(prev => {
          const updatedPhotos = [...prev.photos, ...newPhotos];
          return {
            ...prev,
            photos: updatedPhotos,
            // Set thumbnail to first photo if none is set
            thumbnail: prev.thumbnail || (updatedPhotos.length > 0 ? updatedPhotos[0] : null)
          };
        });

        setSuccess(`${validFiles.length} image(s) uploaded successfully!`);
      } else {
        throw new Error(response?.message || "Failed to upload images");
      }
    } catch (err) {
      console.error('Error uploading files:', err);
      setError(err.message || "Failed to upload images");
    } finally {
      setUploadLoading(false);
    }
  };

  const openEditModal = (gallery) => {
    setSelectedGallery(gallery);
    setFormData({
      title: gallery.title || "",
      description: gallery.description || "",
      photos: gallery.photos || [],
      thumbnail: gallery.thumbnail || null,
    });
    
    // Set thumbnail index
    if (gallery.thumbnail && gallery.photos) {
      const thumbIndex = gallery.photos.findIndex(photo => 
        photo.imageData === gallery.thumbnail.imageData
      );
      setSelectedThumbnailIndex(thumbIndex >= 0 ? thumbIndex : 0);
    } else {
      setSelectedThumbnailIndex(0);
    }
    
    setShowFormModal(true);
  };

  const openCreateModal = () => {
    setSelectedGallery(null);
    setFormData({
      title: "",
      description: "",
      photos: [],
      thumbnail: null,
    });
    setSelectedThumbnailIndex(0);
    setShowFormModal(true);
  };

  const openPreviewModal = (gallery, index = 0) => {
    setSelectedGallery(gallery);
    setSelectedImageIndex(index);
    setShowPreviewModal(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getDisplayThumbnail = (gallery) => {
    if (gallery.thumbnail && gallery.thumbnail.imageUrl) {
      return gallery.thumbnail.imageUrl;
    }
    if (gallery.photos && gallery.photos.length > 0) {
      return gallery.photos[0].imageUrl;
    }
    return null;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchGalleries();
  };

  return (
    <div className="min-h-screen bg-red-50 p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-black">
          Marathi Photo Gallery Management
        </h1>
        <button
          onClick={openCreateModal}
          className="flex items-center px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 transition-colors"
        >
          <PlusCircle className="mr-2" size={20} />
          Add New Gallery
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <form onSubmit={handleSearch} className="flex max-w-md">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search galleries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-red-300 rounded-l-lg focus:outline-none focus:ring-red-500 focus:border-red-500"
            />
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-red-600 text-white rounded-r-lg hover:bg-red-700 transition-colors"
          >
            Search
          </button>
        </form>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-4 bg-green-50 text-green-700 border-green-200">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="animate-spin text-red-600" size={32} />
        </div>
      )}

      {/* Gallery Grid */}
      {!loading && (
        <div className="space-y-6">
          {/* Gallery Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleries.length > 0 ? (
              galleries.map((gallery) => (
                <div
                  key={gallery._id}
                  className="bg-white rounded-lg shadow-lg overflow-hidden border border-red-200 hover:shadow-xl transition-shadow"
                >
                  {/* Gallery Preview */}
                  <div className="relative h-56 overflow-hidden bg-gray-100">
                    {getDisplayThumbnail(gallery) ? (
                      <img
                        src={getDisplayThumbnail(gallery)}
                        alt={gallery.title}
                        className="w-full h-full object-cover cursor-pointer"
                        onClick={() => openPreviewModal(gallery)}
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <Image className="text-gray-400" size={48} />
                      </div>
                    )}

                    {gallery.photos && gallery.photos.length > 1 && (
                      <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded-lg">
                        +{gallery.photos.length - 1} more
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-xl font-semibold text-black">
                        {gallery.title}
                      </h3>
                    </div>

                    {/* Description */}
                    <p className="text-gray-600 mb-3 line-clamp-2">
                      {gallery.description}
                    </p>

                    {/* Metadata */}
                    <div className="space-y-2 mb-3">
                      <div className="flex items-center text-gray-600">
                        <Calendar size={16} className="mr-2 text-red-600" />
                        {formatDate(gallery.createdAt)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {gallery.photos?.length || 0} photo{gallery.photos?.length !== 1 ? 's' : ''}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-2 mt-3">
                      <button
                        onClick={() => openEditModal(gallery)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                        title="Edit gallery"
                      >
                        <Edit2 size={20} />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedGallery(gallery);
                          setShowDeleteModal(true);
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                        title="Delete gallery"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full text-center py-12 bg-white rounded-lg shadow">
                <Image className="mx-auto text-red-300 mb-4" size={64} />
                <p className="text-red-700">
                  {searchTerm ? `No galleries found matching "${searchTerm}"` : "No photo galleries found. Create one to get started!"}
                </p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {galleries.length > 0 && totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-red-200 pt-4">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-red-700">
                    Showing{" "}
                    <span className="font-medium">
                      {(currentPage - 1) * itemsPerPage + 1}
                    </span>{" "}
                    to{" "}
                    <span className="font-medium">
                      {Math.min(currentPage * itemsPerPage, galleries.length)}
                    </span>{" "}
                    of <span className="font-medium">{galleries.length}</span>{" "}
                    results
                  </p>
                </div>
                <div>
                  <nav
                    className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                    aria-label="Pagination"
                  >
                    <button
                      onClick={() => goToPage(1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-red-300 bg-white text-sm font-medium text-red-500 hover:bg-red-50 disabled:opacity-50"
                    >
                      <span className="sr-only">First</span>
                      <ChevronsLeft size={16} />
                    </button>
                    <button
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 border border-red-300 bg-white text-sm font-medium text-red-500 hover:bg-red-50 disabled:opacity-50"
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeft size={16} />
                    </button>
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let page;
                      if (totalPages <= 5) {
                        page = i + 1;
                      } else if (currentPage <= 3) {
                        page = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        page = totalPages - 4 + i;
                      } else {
                        page = currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={page}
                          onClick={() => goToPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === page
                              ? "z-10 bg-red-50 border-red-500 text-red-600"
                              : "bg-white border-red-300 text-red-500 hover:bg-red-50"
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 border border-red-300 bg-white text-sm font-medium text-red-500 hover:bg-red-50 disabled:opacity-50"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRight size={16} />
                    </button>
                    <button
                      onClick={() => goToPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-red-300 bg-white text-sm font-medium text-red-500 hover:bg-red-50 disabled:opacity-50"
                    >
                      <span className="sr-only">Last</span>
                      <ChevronsRight size={16} />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Delete Gallery
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{selectedGallery?.title}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                disabled={loading}
              >
                {loading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gallery Form Modal */}
      {showFormModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-black">
                {selectedGallery ? "Edit Gallery" : "Add New Gallery"}
              </h3>
              <button
                onClick={() => setShowFormModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label
                    htmlFor="title"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Title *
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-red-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="description"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    rows={3}
                    value={formData.description}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-red-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                  />
                </div>
              </div>

              {/* Upload Photos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Photos
                </label>
                <div className="flex items-center justify-center w-full">
                  <label className="flex flex-col w-full h-32 border-2 border-red-300 border-dashed hover:bg-red-50 hover:border-red-400 rounded-lg cursor-pointer">
                    <div className="flex flex-col items-center justify-center pt-7">
                      <Upload className="w-8 h-8 text-red-400" />
                      <p className="pt-1 text-sm text-gray-600">
                        {uploadLoading
                          ? "Uploading..."
                          : "Drag & drop photos or click to upload"}
                      </p>
                    </div>
                    <input
                      type="file"
                      className="opacity-0"
                      multiple
                      accept="image/*"
                      onChange={handleFileUpload}
                      disabled={uploadLoading}
                    />
                  </label>
                </div>
              </div>

              {/* Photo Preview with Thumbnail Selection */}
              {formData.photos.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Gallery Photos ({formData.photos.length})
                  </h4>
                  <p className="text-xs text-gray-500 mb-3">
                    Click on a photo to set it as the gallery thumbnail
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {formData.photos.map((photo, index) => (
                      <div
                        key={index}
                        className={`relative group rounded-lg overflow-hidden border-2 cursor-pointer transition-all ${
                          formData.thumbnail && formData.thumbnail.imageData === photo.imageData
                            ? 'border-green-500 bg-green-50' 
                            : 'border-red-200 hover:border-red-400'
                        }`}
                        onClick={() => setThumbnail(photo, index)}
                      >
                        <img
                          src={photo.imageUrl}
                          alt={photo.caption || `Photo ${index + 1}`}
                          className="w-full h-32 object-cover"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              removePhoto(index);
                            }}
                            className="self-end p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                          >
                            <X size={16} />
                          </button>
                          <div>
                            {photo.caption && (
                              <p className="text-white text-xs truncate">
                                {photo.caption}
                              </p>
                            )}
                          </div>
                        </div>
                        {formData.thumbnail && formData.thumbnail.imageData === photo.imageData && (
                          <div className="absolute top-2 left-2">
                            <CheckCircle className="text-green-500 bg-white rounded-full" size={20} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {formData.thumbnail && (
                    <p className="text-sm text-green-600 mt-2">
                      ✓ Thumbnail selected
                    </p>
                  )}
                </div>
              )}

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowFormModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formData.photos.length === 0 || loading}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {loading ? (selectedGallery ? "Updating..." : "Creating...") : (selectedGallery ? "Update Gallery" : "Create Gallery")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Gallery Preview Modal */}
      {showPreviewModal &&
        selectedGallery &&
        selectedGallery.photos &&
        selectedGallery.photos.length > 0 && (
          <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-4 z-50">
            <div className="relative max-w-4xl w-full">
              <button
                onClick={() => setShowPreviewModal(false)}
                className="absolute top-4 right-4 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 z-10"
              >
                <X size={24} />
              </button>

              {/* Main Image */}
              <div className="relative">
                <img
                  src={selectedGallery.photos[selectedImageIndex].imageUrl}
                  alt={
                    selectedGallery.photos[selectedImageIndex].caption ||
                    selectedGallery.title
                  }
                  className="w-full max-h-[70vh] object-contain"
                />

                {/* Caption */}
                {selectedGallery.photos[selectedImageIndex].caption && (
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-black bg-opacity-60 text-white">
                    {selectedGallery.photos[selectedImageIndex].caption}
                  </div>
                )}

                {/* Navigation */}
                {selectedGallery.photos.length > 1 && (
                  <>
                    <button
                      onClick={() =>
                        setSelectedImageIndex((prev) =>
                          prev === 0
                            ? selectedGallery.photos.length - 1
                            : prev - 1
                        )
                      }
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70"
                    >
                      <ChevronLeft size={24} />
                    </button>
                    <button
                      onClick={() =>
                        setSelectedImageIndex((prev) =>
                          prev === selectedGallery.photos.length - 1
                            ? 0
                            : prev + 1
                        )
                      }
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70"
                    >
                      <ChevronRight size={24} />
                    </button>
                  </>
                )}
              </div>

              {/* Thumbnails */}
              {selectedGallery.photos.length > 1 && (
                <div className="flex overflow-x-auto space-x-2 mt-4 py-2">
                  {selectedGallery.photos.map((photo, index) => (
                    <div
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`relative flex-shrink-0 w-20 h-20 cursor-pointer ${
                        selectedImageIndex === index
                          ? "ring-2 ring-red-500"
                          : "opacity-70"
                      }`}
                    >
                      <img
                        src={photo.imageUrl}
                        alt={photo.caption || `Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover rounded"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
    </div>
  );
};

export default MarathiPhotoGallery;